import { InputNumber } from "@/components/InputNumber";
import { datePickerFormItemProps, datePickerStandardProps, selectStandardProps } from "@/utils/componentUtils";
import { formatLicensePlate } from "@/utils/formatUtils";
import { upperCaseStringNormalizeFunction } from "@/utils/formUtils";
import { isDeepEqual } from "@/utils/utils";
import { regexPatterns, validations } from "@/utils/validationUtils";
import { AutoComplete, Card, Checkbox, Col, DatePicker, Divider, Form, Input, Row, Select } from "antd";
import { Rule } from "antd/lib/form";
import { RcFile } from "antd/lib/upload";
import dayjs from "dayjs";
import { useTranslation } from "react-i18next";
import t from "../../../../../../../app/i18n";
import HiddenInput from "../../../../../../../common/components/form/components/HiddenInput";
import LabelWithTooltip from "../../../../../../../common/components/form/labels/LabelWithTooltip";
import { rowGutter } from "../../../../../../../common/constants";
import { InsuranceCoverageType, VehicleCategory } from "../../../../../../contract/enums";
import VehicleColorSelect from "../../../../../../enumerations/components/form/VehicleColorSelect";
import { VehicleBrandEnumName } from "../../../../../../enumerations/components/view/VehicleBrandEnumName";
import { VehicleModelEnumName } from "../../../../../../enumerations/components/view/VehicleModelEnumName";
import { InstitutionEnum } from "../../../../../../institution/enums";
import { CalcType } from "../../../../../enums";
import { CalcAttachmentUpload } from "../../../../components/CalcAttachmentUpload";
import { CalcAttachmentType } from "../../../../enums";
import { CalcResult } from "../../../../types";
import { VehicleEquipmentType, VehiclePriceSource, VehicleSecurityFeature } from "../../../enums";
import { CrashCalcResultData, VehicleCalc, VehicleCalcResultData, VehicleEquipmentInfo } from "../../../types";
import { newVehicleInKpasKoop, SPECS_MAP, VEHICLE_CATEGORY_M1_N1, VEHICLE_CATEGORY_SELF_MOVING } from "../../../utils";

const damagedAllowedRule = (calcType: CalcType, institutionEnum?: InstitutionEnum): Rule => ({
  validator: (_, value) =>
    value && calcType === CalcType.MTPL_CRASH && institutionEnum === InstitutionEnum.KOOPERATIVA
      ? Promise.reject(t("calc.vehicle.validations.damagedNotAllowed"))
      : Promise.resolve(),
});

const allKeysAvailableRule = (calcType: CalcType, institutionEnum: InstitutionEnum): Rule => ({
  validator: (_, value) =>
    !value && calcType === CalcType.MTPL_CRASH && institutionEnum === InstitutionEnum.KOOPERATIVA
      ? Promise.reject(t("calc.vehicle.validations.allKeysShouldBeAvailable"))
      : Promise.resolve(),
});

const allServiceInspectionsPassedRule: Rule = {
  validator: (_, value) =>
    value ? Promise.resolve() : Promise.reject(t("calc.vehicle.validations.allServiceInspectionsShouldBePassed")),
};

const colSpan = 4;

interface Props {
  calcData: VehicleCalc;
  selectedResult: CalcResult<VehicleCalcResultData>;
  onCalcAttachmentChange: (type: CalcAttachmentType, file?: RcFile) => void;
}

const VehicleGenVehicleDataSection = ({ calcData, selectedResult, onCalcAttachmentChange }: Props) => {
  const { t } = useTranslation();

  const { vehicleData } = calcData;
  const { calcType } = selectedResult;
  const { institutionEnum } = selectedResult.insuranceInstitution;

  const priceFromKpasKoopPriceList = !!(selectedResult.data as CrashCalcResultData).vehiclePriceFromKpasKoopPriceList;

  return (
    <Card type="inner" className="card-box" title={t("calc.vehicle.sections.additionalVehicleData")}>
      <div className="sub-header-info normal-font-size margin-bottom-small">
        <span>{t("calc.vehicle.sections.vehicleData")}: </span>
        <VehicleBrandEnumName brandId={vehicleData.brandId} otherBrandName={vehicleData.customBrand} />
        &nbsp;
        <VehicleModelEnumName modelId={vehicleData.modelId} otherModelName={vehicleData.customModel} />
        ,&nbsp;
        {t("calc.vehicle.attrs.vehicleData.licensePlate")} -{" "}
        {vehicleData.licensePlate ? formatLicensePlate(vehicleData.licensePlate) : t("common.notSpecified")}
        ,&nbsp;
        {t("calc.vehicle.attrs.vehicleData.vin")} - {vehicleData.vin}
      </div>

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <Form.Item
            name={["vehicleData", "registrationCertificatePresent"]}
            className="form-item-without-label"
            valuePropName="checked"
            rules={[validations.none]}
            initialValue={false}
          >
            <Checkbox>{t("calc.vehicle.attrs.vehicleData.registrationCertificatePresent")}</Checkbox>
          </Form.Item>
        </Col>

        <Form.Item
          noStyle
          shouldUpdate={(prev, next) =>
            prev.vehicleData.registrationCertificatePresent !== next.vehicleData.registrationCertificatePresent
          }
        >
          {({ getFieldValue }) =>
            getFieldValue(["vehicleData", "registrationCertificatePresent"]) && (
              <Col span={colSpan}>
                <Form.Item
                  name={["vehicleData", "registrationCertificateNumber"]}
                  label={
                    <LabelWithTooltip
                      label={t("calc.vehicle.attrs.vehicleData.registrationCertificateNumber")}
                      tooltip={t("calc.vehicle.helpers.registrationCertificateNumberDesc")}
                    />
                  }
                  rules={[
                    validations.notBlank,
                    validations.pattern(regexPatterns.registrationCertificateNumberRegex),
                    validations.length(8),
                  ]}
                  normalize={upperCaseStringNormalizeFunction}
                >
                  {vehicleData.previousRegistrationCertificateNumber ? (
                    <AutoComplete
                      options={[
                        {
                          value: vehicleData.previousRegistrationCertificateNumber,
                          label: vehicleData.previousRegistrationCertificateNumber,
                        },
                      ]}
                      optionRender={(option) => (
                        <>
                          <span className="sub-header-info">
                            {t("calc.vehicle.attrs.vehicleData.previousRegistrationCertificateNumber")}
                          </span>
                          <br />
                          {option.value}
                        </>
                      )}
                    />
                  ) : (
                    <Input />
                  )}
                </Form.Item>
              </Col>
            )
          }
        </Form.Item>
      </Row>

      {calcType === CalcType.MTPL || calcType === CalcType.CRASH || calcType === CalcType.MTPL_CRASH ? (
        <Row gutter={rowGutter}>
          <Col span={colSpan}>
            <Form.Item
              name={["vehicleData", "damaged"]}
              className="form-item-without-label"
              valuePropName="checked"
              rules={[damagedAllowedRule(calcType, institutionEnum)]}
              initialValue={false}
            >
              <Checkbox>{t("calc.vehicle.attrs.vehicleData.damaged")}</Checkbox>
            </Form.Item>
          </Col>

          <Form.Item noStyle shouldUpdate={(prev, next) => prev.vehicleData.damaged !== next.vehicleData.damaged}>
            {({ getFieldValue }) =>
              getFieldValue(["vehicleData", "damaged"]) && (
                <Col span={colSpan}>
                  <Form.Item
                    name={["vehicleData", "damageDescription"]}
                    label={t("calc.vehicle.attrs.vehicleData.damageDescription")}
                    rules={[validations.notBlank, validations.size(1, 255)]}
                  >
                    <Input />
                  </Form.Item>
                </Col>
              )
            }
          </Form.Item>
        </Row>
      ) : null}

      {institutionEnum === InstitutionEnum.KOOPERATIVA &&
        calcType === CalcType.MTPL &&
        vehicleData.category === VehicleCategory.TRAILER_O && (
          <Row gutter={rowGutter}>
            <Col span={colSpan * 3}>
              <Form.Item
                name={["generalData", "semiTrailerVehicleIsAlsoInsured"]}
                valuePropName="checked"
                rules={[validations.trueValue]}
                initialValue={false}
              >
                <Checkbox>
                  <LabelWithTooltip
                    label={t("calc.vehicle.attrs.generalData.semiTrailerVehicleIsAlsoInsured")}
                    tooltip={t("calc.vehicle.helpers.semiTrailerVehicleIsAlsoInsuredDesc")}
                  />
                </Checkbox>
              </Form.Item>
            </Col>
          </Row>
        )}

      {calcType === CalcType.MTPL_CRASH && institutionEnum === InstitutionEnum.KOOPERATIVA && (
        <>
          <Row gutter={rowGutter}>
            <Col span={colSpan * 3}>
              <Form.Item
                name={["vehicleData", "allKeysAvailable"]}
                valuePropName="checked"
                rules={[allKeysAvailableRule(calcType, institutionEnum)]}
                initialValue={false}
              >
                <Checkbox>{t("calc.vehicle.attrs.vehicleData.allKeysAvailable")}</Checkbox>
              </Form.Item>
            </Col>
          </Row>
          {selectedResult.data?.appliedCoverages.some(
            (coverage) => coverage.type === InsuranceCoverageType.KOOP_EXTENDED_WARRANTY,
          ) && (
            <Row gutter={rowGutter}>
              <Col span={colSpan * 3}>
                <Form.Item
                  name={["vehicleData", "allServiceInspectionsPassed"]}
                  valuePropName="checked"
                  rules={[allServiceInspectionsPassedRule]}
                  initialValue={false}
                >
                  <Checkbox>{t("calc.vehicle.attrs.vehicleData.allServiceInspectionsPassed")}</Checkbox>
                </Form.Item>
              </Col>
            </Row>
          )}
        </>
      )}

      <Row gutter={rowGutter}>
        <Col span={colSpan}>
          <VehicleColorSelect
            formItemProps={{
              name: ["vehicleData", "colorId"],
              label: t("calc.vehicle.attrs.vehicleData.colorId"),
              rules: [validations.notNull],
              initialValue: vehicleData.colorId,
            }}
          />
        </Col>

        {VEHICLE_CATEGORY_SELF_MOVING.includes(vehicleData.category) && (
          <>
            {((calcType === CalcType.CRASH &&
              (institutionEnum === InstitutionEnum.CSOB ||
                institutionEnum === InstitutionEnum.GENERALI ||
                institutionEnum === InstitutionEnum.KOMUNALNA)) ||
              (calcType === CalcType.MTPL_CRASH && institutionEnum === InstitutionEnum.CSOB)) && (
              <Col span={colSpan}>
                <Form.Item
                  name={["vehicleData", "key", "numberOfPieces"]}
                  label={t("calc.vehicle.attrs.vehicleData.key.numberOfPieces")}
                  rules={[validations.notNull, validations.minNumber(1), validations.maxNumber(100)]}
                >
                  <InputNumber />
                </Form.Item>
              </Col>
            )}

            {(institutionEnum === InstitutionEnum.GENERALI || institutionEnum === InstitutionEnum.KOMUNALNA) &&
              calcType === CalcType.CRASH && (
                <Col span={colSpan}>
                  <Form.Item
                    name={["vehicleData", "key", "numberOfCopies"]}
                    label={t("calc.vehicle.attrs.vehicleData.key.numberOfCopies")}
                    rules={[validations.notNull, validations.minNumber(0), validations.maxNumber(100)]}
                  >
                    <InputNumber />
                  </Form.Item>
                </Col>
              )}
          </>
        )}

        {(calcType === CalcType.CRASH || calcType === CalcType.MTPL_CRASH) &&
          (institutionEnum === InstitutionEnum.KOMUNALNA || institutionEnum === InstitutionEnum.KOOPERATIVA) && (
            <Col span={colSpan}>
              <Form.Item
                name={["vehicleData", "priceSource"]}
                label={t("calc.vehicle.enums.vehiclePriceSource._label")}
                rules={[validations.notNull]}
                initialValue={
                  priceFromKpasKoopPriceList
                    ? VehiclePriceSource.KPAS_KOOP_PRICE_LIST
                    : newVehicleInKpasKoop(vehicleData.newVehicle, vehicleData.odometer)
                      ? VehiclePriceSource.KPAS_KOOP_INVOICE
                      : undefined
                }
              >
                <Select
                  {...selectStandardProps}
                  disabled={
                    priceFromKpasKoopPriceList || newVehicleInKpasKoop(vehicleData.newVehicle, vehicleData.odometer)
                  }
                  options={(priceFromKpasKoopPriceList
                    ? [VehiclePriceSource.KPAS_KOOP_PRICE_LIST]
                    : newVehicleInKpasKoop(vehicleData.newVehicle, vehicleData.odometer)
                      ? [VehiclePriceSource.KPAS_KOOP_INVOICE]
                      : [VehiclePriceSource.KPAS_KOOP_INVOICE, VehiclePriceSource.KPAS_KOOP_SLOVEXPERTA]
                  ).map((source) => ({
                    value: source,
                    label: t("calc.vehicle.enums.vehiclePriceSource." + source),
                  }))}
                />
              </Form.Item>
            </Col>
          )}

        {(calcType === CalcType.CRASH || calcType === CalcType.MTPL_CRASH) &&
          (institutionEnum === InstitutionEnum.KOMUNALNA || institutionEnum === InstitutionEnum.KOOPERATIVA) && (
            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => prev.vehicleData.priceSource !== next.vehicleData.priceSource}
            >
              {({ getFieldValue }) => {
                const priceSource = getFieldValue(["vehicleData", "priceSource"]) as VehiclePriceSource;

                if (priceSource === VehiclePriceSource.KPAS_KOOP_INVOICE) {
                  return (
                    <Col span={colSpan}>
                      <Form.Item
                        name={["vehicleData", "invoiceIssueDate"]}
                        label={t("calc.vehicle.attrs.vehicleData.invoiceIssueDate")}
                        rules={[validations.notNull, validations.notFuture]}
                        {...datePickerFormItemProps}
                      >
                        <DatePicker {...datePickerStandardProps} maxDate={dayjs()} />
                      </Form.Item>
                    </Col>
                  );
                }

                if (priceSource === VehiclePriceSource.KPAS_KOOP_SLOVEXPERTA) {
                  return (
                    <>
                      <Col span={colSpan}>
                        <Form.Item
                          name={["vehicleData", "slovexpertaIssuerName"]}
                          label={t("calc.vehicle.attrs.vehicleData.slovexpertaIssuerName")}
                          rules={[validations.notBlank, validations.size(1, 64)]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>

                      <Col span={colSpan}>
                        <Form.Item
                          name={["vehicleData", "slovexpertaIssueDate"]}
                          label={t("calc.vehicle.attrs.vehicleData.slovexpertaIssueDate")}
                          rules={[validations.notNull, validations.notFuture]}
                          {...datePickerFormItemProps}
                        >
                          <DatePicker {...datePickerStandardProps} maxDate={dayjs()} />
                        </Form.Item>
                      </Col>
                    </>
                  );
                }

                return null;
              }}
            </Form.Item>
          )}

        {institutionEnum === InstitutionEnum.ALLIANZ &&
          calcData.type === CalcType.MTPL &&
          SPECS_MAP.get("odometer")?.includes(vehicleData.category) && (
            <Col span={colSpan}>
              <Form.Item
                name={["vehicleData", "odometer"]}
                label={t("calc.vehicle.attrs.vehicleData.odometer")}
                rules={[validations.notNull, validations.minNumber(0)]}
              >
                <InputNumber addon="distance" />
              </Form.Item>
            </Col>
          )}

        {institutionEnum === InstitutionEnum.COLONNADE && calcType === CalcType.GAP && (
          <Col span={colSpan}>
            <Form.Item
              name={["vehicleData", "purchaseDate"]}
              label={
                <LabelWithTooltip
                  label={t("calc.vehicle.attrs.vehicleData.purchaseDate")}
                  tooltip={t("calc.vehicle.helpers.purchaseDateDesc")}
                />
              }
              rules={[validations.notNull, validations.dateInInterval(dayjs().subtract(6, "month"), dayjs())]}
              {...datePickerFormItemProps}
            >
              <DatePicker {...datePickerStandardProps} minDate={dayjs().subtract(6, "month")} maxDate={dayjs()} />
            </Form.Item>
          </Col>
        )}
      </Row>

      {institutionEnum === InstitutionEnum.UNION &&
        (calcType === CalcType.CRASH || calcType === CalcType.MTPL_CRASH) &&
        VEHICLE_CATEGORY_M1_N1.includes(calcData.vehicleData.category) && (
          <>
            <Divider titlePlacement="start" className="divider-subheader">
              {t("calc.vehicle.sections.vehicleEquipment")}
            </Divider>

            <Form.Item
              noStyle
              shouldUpdate={(prev, next) => isDeepEqual(prev.vehicleData.equipment, next.vehicleData.equipment)}
            >
              {({ getFieldValue }) => {
                const equipment = (getFieldValue(["vehicleData", "equipment"]) || []) as VehicleEquipmentInfo[];
                const otherEquipmentSelected = equipment.find(
                  (e) => e.type === VehicleEquipmentType.OTHER && e.selected,
                );

                return Object.values(VehicleEquipmentType).map((type, index) => (
                  <Row gutter={rowGutter} key={type}>
                    <HiddenInput name={["vehicleData", "equipment", index, "type"]} initialValue={type} />

                    <Col span={colSpan}>
                      <Form.Item
                        name={["vehicleData", "equipment", index, "selected"]}
                        className={
                          type === VehicleEquipmentType.OTHER && otherEquipmentSelected
                            ? "form-item-without-label"
                            : undefined
                        }
                        valuePropName="checked"
                        rules={[validations.none]}
                        initialValue={false}
                      >
                        <Checkbox>{t("calc.vehicle.enums.vehicleEquipmentType." + type)}</Checkbox>
                      </Form.Item>
                    </Col>

                    {type === VehicleEquipmentType.OTHER && otherEquipmentSelected && (
                      <Col span={colSpan}>
                        <Form.Item
                          name={["vehicleData", "equipment", index, "description"]}
                          label={t("calc.vehicle.attrs.vehicleData.equipment.description")}
                          rules={[validations.notBlank, validations.size(1, 255)]}
                        >
                          <Input />
                        </Form.Item>
                      </Col>
                    )}
                  </Row>
                ));
              }}
            </Form.Item>
          </>
        )}

      {calcType === CalcType.CRASH &&
        vehicleData.security
          .filter((security) => security.selected)
          .find(
            (s) => institutionEnum === InstitutionEnum.KOMUNALNA && s.feature === VehicleSecurityFeature.MECHANICAL,
          ) && (
          <>
            <Divider titlePlacement="start" className="divider-subheader">
              {t("calc.vehicle.sections.vehicleSecurity")}
            </Divider>

            {vehicleData.security.map((security, index) => {
              const kpasNumberOfKeys =
                institutionEnum === InstitutionEnum.KOMUNALNA && security.feature === VehicleSecurityFeature.MECHANICAL;

              return (
                <Row gutter={rowGutter} key={security.feature}>
                  <HiddenInput name={["vehicleData", "security", index, "feature"]} initialValue={security.feature} />

                  {security.selected && kpasNumberOfKeys && (
                    <>
                      <Col span={colSpan} className="form-item-without-label">
                        {t("calc.vehicle.enums.vehicleSecurityFeature." + security.feature)}:
                      </Col>

                      <Col span={colSpan + 1}>
                        <Form.Item
                          name={["vehicleData", "security", index, "numberOfKeysOrControllers"]}
                          label={t("calc.vehicle.attrs.vehicleData.security.numberOfKeysOrControllers")}
                          rules={[validations.notNull, validations.minNumber(1), validations.maxNumber(100)]}
                        >
                          <InputNumber />
                        </Form.Item>
                      </Col>
                    </>
                  )}
                </Row>
              );
            })}
          </>
        )}

      {(institutionEnum === InstitutionEnum.KOOPERATIVA || institutionEnum === InstitutionEnum.KOMUNALNA) &&
        (calcType === CalcType.CRASH || calcType === CalcType.MTPL_CRASH) && (
          <Form.Item
            noStyle
            shouldUpdate={(prev, next) =>
              prev.vehicleData.registrationCertificatePresent !== next.vehicleData.registrationCertificatePresent
            }
          >
            {({ getFieldValue }) =>
              (getFieldValue(["vehicleData", "registrationCertificatePresent"]) ||
                newVehicleInKpasKoop(vehicleData.newVehicle, vehicleData.odometer)) && (
                <>
                  <Divider titlePlacement="start" className="divider-subheader">
                    {t("calc.vehicle.sections.vehicleAttachments")}
                  </Divider>

                  <Row gutter={rowGutter}>
                    {getFieldValue(["vehicleData", "registrationCertificatePresent"]) && (
                      <Col span={colSpan * 2}>
                        <CalcAttachmentUpload
                          type={CalcAttachmentType.REGISTRATION_CERTIFICATE}
                          onChange={onCalcAttachmentChange}
                        />
                      </Col>
                    )}

                    {newVehicleInKpasKoop(vehicleData.newVehicle, vehicleData.odometer) && (
                      <>
                        <Col span={colSpan * 2}>
                          <CalcAttachmentUpload
                            type={CalcAttachmentType.VEHICLE_INVOICE}
                            onChange={onCalcAttachmentChange}
                          />
                        </Col>

                        {institutionEnum === InstitutionEnum.KOMUNALNA && (
                          <Col span={colSpan * 2}>
                            <CalcAttachmentUpload
                              type={CalcAttachmentType.ACCEPTANCE_PROTOCOL}
                              onChange={onCalcAttachmentChange}
                            />
                          </Col>
                        )}
                      </>
                    )}

                    {!newVehicleInKpasKoop(vehicleData.newVehicle, vehicleData.odometer) &&
                      institutionEnum === InstitutionEnum.KOOPERATIVA && (
                        <Form.Item
                          noStyle
                          shouldUpdate={(prev, next) => prev.vehicleData.priceSource !== next.vehicleData.priceSource}
                        >
                          {({ getFieldValue }) =>
                            getFieldValue(["vehicleData", "priceSource"]) === VehiclePriceSource.KPAS_KOOP_INVOICE && (
                              <Col span={colSpan * 2}>
                                <CalcAttachmentUpload
                                  type={CalcAttachmentType.VEHICLE_INVOICE}
                                  onChange={onCalcAttachmentChange}
                                />
                              </Col>
                            )
                          }
                        </Form.Item>
                      )}
                  </Row>
                </>
              )
            }
          </Form.Item>
        )}

      {calcType === CalcType.CRASH && institutionEnum === InstitutionEnum.CSOB && (
        <Form.Item
          noStyle
          shouldUpdate={(prev, next) =>
            prev.vehicleData.registrationCertificatePresent !== next.vehicleData.registrationCertificatePresent
          }
        >
          {({ getFieldValue }) => {
            const registrationCertificatePresent = getFieldValue(["vehicleData", "registrationCertificatePresent"]);
            return (
              (vehicleData.newVehicle || registrationCertificatePresent) && (
                <>
                  <Divider titlePlacement="start" className="divider-subheader">
                    {t("calc.vehicle.sections.vehicleAttachments")}
                  </Divider>

                  <Row gutter={rowGutter}>
                    {vehicleData.newVehicle && (
                      <Col span={colSpan * 2}>
                        <CalcAttachmentUpload
                          type={CalcAttachmentType.VEHICLE_INVOICE}
                          onChange={onCalcAttachmentChange}
                        />
                      </Col>
                    )}

                    {registrationCertificatePresent && (
                      <Col span={colSpan * 2}>
                        <CalcAttachmentUpload
                          type={CalcAttachmentType.REGISTRATION_CERTIFICATE}
                          onChange={onCalcAttachmentChange}
                        />
                      </Col>
                    )}
                  </Row>
                </>
              )
            );
          }}
        </Form.Item>
      )}
    </Card>
  );
};

export default VehicleGenVehicleDataSection;
